import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useDataGridTextFilter } from '../../../../common/DataGrid/filters/useDataGridTextFilter';
import { dataGridTextSorter } from '../../../../common/DataGrid/sorters/DataGridSorters';
import DataGrid from '../../../../common/DataGrid/DataGrid';
import { CollapseBlock, Tooltip } from '../../../../common';
import { Flex, Grid, Radio, Select, Steps } from 'antd';
import { AuditOutlined } from '@ant-design/icons';
import { IconButton } from '../../../../common/controls/Controls';
import useIsUserAdmin from '../../../../../hooks/useIsUserAdmin';
import checkPermission from '../../../../../utils/helpers/checkPermission';

const getChallengeLabel = (challenge) =>
  [challenge?.name, challenge?.academic_year].filter(Boolean).join(' - ');

const ErasmusMobilitiesTable = ({
  erasmusMobilities,
  challengeOptions,
  selectedChallenge,
  setSelectedChallenge,
  onDelete,
  onDetailClick,
  onPreviewClick,
}) => {
  const { t } = useTranslation();
  const screens = Grid.useBreakpoint();
  const user = useSelector((state) => state.auth.user);
  const isAdmin = useIsUserAdmin();
  const NameFilterProps = useDataGridTextFilter('last_name');
  const UniversityNameFilterProps = useDataGridTextFilter('university_name');
  const CountryFilterProps = useDataGridTextFilter('country');

  const mobilitySteps = useMemo(
    () => [
      { key: 'ola_path', title: 'OLA', short: 'OLA' },
      {
        key: 'financial_contract_path',
        title: 'Finančná zmluva',
        short: 'Fin. zml.',
      },
      {
        key: 'certificate_of_arrival_path',
        title: 'Potvrdenie o príchode',
        short: 'Príchod',
      },
      {
        key: 'certificate_of_completion_path',
        title: 'Potvrdenie o ukončení mobility',
        short: 'Ukončen.',
      },
      { key: 'grades_path', title: 'Študijné výsledky', short: 'Výsledky' },
      { key: 'questionnaire', title: 'Dotazník', short: 'Dotazník' },
      {
        key: 'recognition_of_subjects_path',
        title: 'Uznanie predmetov',
        short: 'Uz. pred.',
      },
      {
        key: 'recognition_of_study_path',
        title: 'Uznanie mobility',
        short: 'Uz. mob.',
      },
    ],
    [],
  );

  const getMobilityStepItems = (record) =>
    mobilitySteps.map((step, index) => ({
      title: (
        <Tooltip title={t(step.title)}>
          <span
            style={{
              display: 'inline-block',
              whiteSpace: 'nowrap',
              textAlign: 'center',
              lineHeight: 1.15,
              fontSize: 12,
              cursor: 'pointer',
            }}
            onClickCapture={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onDetailClick({ ...record, initialStepIndex: index });
            }}
            onMouseDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            onMouseUp={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
          >
            {t(step.short)}
          </span>
        </Tooltip>
      ),
    }));

  const getCellClickProps = (record) => ({
    onClick: (event) => {
      event.stopPropagation();
      onDetailClick(record);
    },
  });

  const calculateStepsState = (mobility) => {
    let completedCount = 0;

    mobilitySteps.forEach((step) => {
      if (mobility[step.key]) {
        completedCount += 1;
      }
    });

    const current = Math.min(completedCount, mobilitySteps.length - 1);
    const status =
      completedCount === 0
        ? 'wait'
        : completedCount === mobilitySteps.length
          ? 'finish'
          : 'process';
    return { current, status };
  };

  const [selectedOption, setSelectedOption] = useState('active');

  const handleRadioChange = (e) => {
    setSelectedOption(e.target.value);
  };

  const filteredMobilities = useMemo(() => {
    if (!erasmusMobilities) return [];
    let filtered = erasmusMobilities;
    if (selectedOption === 'archived') {
      filtered = filtered.filter(
        (mobility) => mobility.application.archived_at,
      );
    } else {
      filtered = filtered.filter(
        (mobility) => !mobility.application.archived_at,
      );
    }

    if (selectedChallenge && selectedChallenge !== '') {
      filtered = filtered.filter(
        (mobility) => mobility.application?.challenge_id === selectedChallenge,
      );
    }

    return filtered;
  }, [erasmusMobilities, selectedOption, selectedChallenge]);

  const challengeSelectOptions = useMemo(() => {
    if (!challengeOptions) return [];
    return challengeOptions.map((challenge) => ({
      value: challenge.id,
      label: getChallengeLabel(challenge),
    }));
  }, [challengeOptions]);

  const columns = useMemo(() => {
    const baseColumns = [
      {
        key: 'name',
        title: t('Priezvisko'),
        dataIndex: ['application', 'person', 'last_name'],
        width: 150,
        ellipsis: true,
        onCell: getCellClickProps,
        sorter: dataGridTextSorter('last_name'),
        defaultSortOrder: 'ascend',
        render: (_, record) =>
          record.application?.person?.last_name || t('Neznámy'),
        ...NameFilterProps,
      },
      {
        key: 'institution',
        title: t('Názov inštitúcie'),
        dataIndex: ['application', 'choosed_university', 'name'],
        width: 220,
        ellipsis: true,
        onCell: getCellClickProps,
        sorter: dataGridTextSorter('university_name'),
        defaultSortOrder: 'ascend',
        render: (_, record) =>
          record.application?.choosed_university?.name || t('Neznámy'),
        ...UniversityNameFilterProps,
      },
      {
        key: 'country',
        title: t('Krajina'),
        dataIndex: ['application', 'choosed_university', 'country'],
        width: 170,
        ellipsis: true,
        onCell: getCellClickProps,
        sorter: dataGridTextSorter('country'),
        defaultSortOrder: 'ascend',
        render: (_, record) =>
          record.application?.choosed_university?.country || t('Neznámy'),
        ...CountryFilterProps,
      },
    ];

    if (selectedOption === 'active') {
      baseColumns.push({
        key: 'currentStep',
        title: t('Aktuálny krok'),
        dataIndex: 'currentStep',
        width: 460,
        onCell: getCellClickProps,
        render: (_, record) => {
          const { current, status } = calculateStepsState(record);
          return (
            <Steps
              size="small"
              style={{ marginTop: 4, width: '100%' }}
              type="inline"
              current={current}
              status={status}
              items={getMobilityStepItems(record)}
            />
          );
        },
      });
    }

    return baseColumns;
  }, [
    NameFilterProps,
    UniversityNameFilterProps,
    CountryFilterProps,
    selectedOption,
    mobilitySteps,
    t,
  ]);

  return (
    <>
      <CollapseBlock collapsible={true} title={t('Filter mobilít')}>
        <Flex
          vertical={!screens.md}
          style={{
            alignItems: screens.md ? 'baseline' : 'stretch',
            justifyContent: 'space-between',
          }}
          gap="middle"
        >
          <Flex>
            <Radio.Group
              block
              value={selectedOption}
              optionType="button"
              onChange={handleRadioChange}
              style={{ whiteSpace: 'nowrap' }}
            >
              <Radio value="active">{t('Aktuálne')}</Radio>
              <Radio value="archived">{t('Archivované')}</Radio>
            </Radio.Group>
          </Flex>
          {challengeSelectOptions.length > 0 && (
            <Flex style={{ alignItems: 'center' }} gap="small">
              <span style={{ fontSize: '14px', fontWeight: '500' }}>
                {t('Výzva')}
              </span>
              <Select
                placeholder={t('Vyberte výzvu')}
                value={selectedChallenge || ''}
                onChange={setSelectedChallenge}
                style={{
                  minWidth: screens.md ? '200px' : 0,
                  flex: screens.md ? undefined : 1,
                }}
                allowClear
                showSearch
                filterOption={(input, option) => {
                  if (!option?.label) return false;
                  return String(option.label)
                    .toLowerCase()
                    .includes(input.toLowerCase());
                }}
                options={challengeSelectOptions}
              />
            </Flex>
          )}
        </Flex>
      </CollapseBlock>
      <DataGrid
        rowKey={(mobility) => mobility.id}
        columns={columns}
        dataSource={filteredMobilities || []}
        tableLayout="fixed"
        permissionsBasic={'erasmus-mobilities'}
        onRowClick={onDetailClick}
        onRowDetailClick={onDetailClick}
        onDelete={onDelete}
        previewTooltip={t('Dokumenty mobility')}
        onPreviewClick={onDetailClick}
        customSettings={(record) =>
          checkPermission(user, 'erasmus-mobilities.questionnaire', isAdmin) ? (
            <IconButton
              icon={<AuditOutlined />}
              tooltip={t('Dotazník')}
              onClick={(event) => {
                event.stopPropagation();
                onPreviewClick(record);
              }}
            />
          ) : null
        }
      />
    </>
  );
};

ErasmusMobilitiesTable.propTypes = {
  erasmusMobilities: PropTypes.array,
  challengeOptions: PropTypes.array,
  selectedChallenge: PropTypes.any,
  setSelectedChallenge: PropTypes.func,
  onDelete: PropTypes.func,
  onDetailClick: PropTypes.func,
  onCreateClick: PropTypes.func,
};

export default ErasmusMobilitiesTable;
